import { Toggle } from '@warp-ds/react/components/toggle';
import { useEffect } from 'react';
import { createMoney, getDisplayCurrency, type Money } from 'shared/util/money';
import { PrivatePriceOverview } from '@/components/priceOverview/PrivatePriceOverview';
import type { PriceCleaningChoice } from '@/domain';
import { usePrivateItemPrice } from '@/hooks/usePrivateItemPrice';

export interface ClassifiedObjectPagePriceOverviewProps {
    itemId: number;
    startDate: Date;
    endDate: Date;
    numberOfGuests: number;
    cleaningChoice: PriceCleaningChoice | null;
    setCleaningChoice: (cleaningChoice: PriceCleaningChoice) => void;
    setPrice: (price: Money) => void;
}

export const ClassifiedObjectPagePriceOverview = ({
    itemId,
    startDate,
    endDate,
    numberOfGuests,
    cleaningChoice,
    setCleaningChoice,
    setPrice,
}: ClassifiedObjectPagePriceOverviewProps) => {
    const {
        privateItemPrice: price,
        error,
        isLoading,
    } = usePrivateItemPrice(itemId.toString(), startDate, endDate, numberOfGuests, cleaningChoice);

    const hasOptionalCleaning = price?.cleaning?.cleaningOption === 'OPTIONAL_GUEST_CLEANS_OR_FEE';

    const toggleCleaningChoice = (wantsToPayForCleaning: boolean) => {
        setCleaningChoice(wantsToPayForCleaning ? 'FEE' : 'GUEST_CLEANS');
    };

    useEffect(() => {
        if (price && setPrice) {
            setPrice({ value: price.total, currency: 'NOK' });
        }
    }, [price]);

    return (
        <PrivatePriceOverview
            startDate={startDate}
            endDate={endDate}
            price={price}
            optionalCleaningDescription="Leietaker vasker"
            error={error}
            isLoading={isLoading}>
            {hasOptionalCleaning && (
                <div className="my-16">
                    <Toggle
                        type="checkbox"
                        label={`Legg til rengjøring (+ ${getDisplayCurrency(
                            createMoney({
                                value: price?.cleaning?.amount,
                                currency: 'NOK',
                            }),
                        )})`}
                        checked={cleaningChoice === 'FEE'}
                        onChange={(checked: boolean) => {
                            toggleCleaningChoice(checked);
                        }}
                        data-testid="objectPageCleaningServiceToggle"
                        helpText="Hvis ikke valgt, må boligen være rengjort ved utsjekk."
                    />
                </div>
            )}
        </PrivatePriceOverview>
    );
};
